// A «buzón»-local calendar day as a bare `YYYY-MM-DD` string — a *floating* date: no instant, no
// offset. It is what «Ampliar custodia» reasons about (a deadline is a day, not a moment) and what
// the write sends. Reducing an instant to a day is timezone-dependent (a parcel expiring at 23:59
// UTC is already the next day in Madrid), so the «buzón» timezone rules here exactly as it rules
// rendering (ADR 0011). Built on Intl, the panel's only timezone tool — the date library the
// calendar needs lives behind the @citibox-panel/ui wrapper and never reaches a slice.
// Distinct from formatDeliveryDateTime: that localizes an INSTANT, this resolves a DAY.
const MS_PER_DAY = 86_400_000;

// formatToParts, not format(): the parts are locale-independent, so the string is assembled here
// instead of trusting a locale's separator to be a dash.
function dayInTimeZone(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const at = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  return `${at('year')}-${at('month')}-${at('day')}`;
}

// Midnight UTC on a floating date. Day arithmetic goes through UTC because a DST transition must
// never move a calendar day, and midnight-to-midnight in a real zone is not always 24 h.
function utcOf(day: string): number {
  return Date.UTC(
    Number(day.slice(0, 4)),
    Number(day.slice(5, 7)) - 1,
    Number(day.slice(8, 10)),
  );
}

// The «buzón»-local day an instant falls on. A null instant stays null (a delivery with no
// recorded expiration is a real state, not an error). An unparseable value degrades to null too,
// so a malformed wire date narrows the feature instead of throwing inside a render.
export function lockerDayOf(iso: string | null, timeZone: string): string | null {
  if (iso === null) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return dayInTimeZone(date, timeZone);
}

export function lockerToday(timeZone: string): string {
  return dayInTimeZone(new Date(), timeZone);
}

export function addDays(day: string, amount: number): string {
  return new Date(utcOf(day) + amount * MS_PER_DAY).toISOString().slice(0, 10);
}

export function diffDays(later: string, earlier: string): number {
  return Math.round((utcOf(later) - utcOf(earlier)) / MS_PER_DAY);
}

// The later of two floating days. A string comparison and not date arithmetic on purpose: the
// zero-padded fixed-width fields of `YYYY-MM-DD` make the lexicographic order the chronological one,
// across month and year boundaries alike, so there is no Date to build and no zone to get wrong.
export function maxDay(a: string, b: string): string {
  return a >= b ? a : b;
}

// `dd/mm/aaaa`, the panel's date shape (cf. formatDeliveryDateTime) — for a day that is already
// resolved, so there is no instant left to localize.
export function formatLockerDay(day: string): string {
  return `${day.slice(8, 10)}/${day.slice(5, 7)}/${day.slice(0, 4)}`;
}
